import React, { useEffect, useState } from 'react'
import { Badge } from '../shared/Badge'
import {
  type MapVisitor,
  friendlyPageLabel,
  visitorLocationLabel,
  visitorDisplayName,
  visitorInitials,
  formatLocalDateTimeWithTz,
  formatHMS,
  secondsSince,
  sessionLengthSeconds,
} from './visitorMap.helpers'
import { formatRelativeTime } from '../../utils/format'

/**
 * Visitor avatar = the organization logo when present, otherwise a circle with
 * the initials of the visitor (or org) name. `size` is the pixel diameter.
 */
export const VisitorAvatar: React.FC<{ visitor: MapVisitor; size?: number }> = ({
  visitor,
  size = 40,
}) => {
  const [imgFailed, setImgFailed] = useState(false)
  const logo = visitor.company_logo_url
  const px = `${size}px`

  if (logo && !imgFailed) {
    return (
      <img
        src={logo}
        alt={visitor.company_name || 'Organization logo'}
        onError={() => setImgFailed(true)}
        style={{ width: px, height: px }}
        className="rounded-full object-cover bg-surface-tertiary border border-border shrink-0"
      />
    )
  }

  return (
    <span
      style={{ width: px, height: px, fontSize: `${Math.round(size * 0.38)}px` }}
      className="inline-flex items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 font-semibold border border-border shrink-0"
      aria-hidden="true"
    >
      {visitorInitials(visitor)}
    </span>
  )
}

/**
 * Live-ticking "time online" / session-length readout.
 *
 * For an online visitor we recompute elapsed-since-`connected_at` every second.
 * For offline we show the fixed session length (disconnect − connect) or, if we
 * can't compute it, "last seen <relative>".
 */
const LiveDuration: React.FC<{ visitor: MapVisitor }> = ({ visitor }) => {
  const isOnline = visitor.status === 'online' || visitor.is_online
  const [, forceTick] = useState(0)

  useEffect(() => {
    if (!isOnline) return
    const id = setInterval(() => forceTick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [isOnline])

  if (isOnline) {
    const secs = secondsSince(visitor.connected_at)
    return <span className="tabular-nums">{secs == null ? '—' : formatHMS(secs)}</span>
  }

  const session = sessionLengthSeconds(visitor.connected_at, visitor.disconnected_at)
  if (session != null) {
    return <span className="tabular-nums">{formatHMS(session)}</span>
  }
  const lastSeen = visitor.last_seen_at || visitor.disconnected_at
  return <span>{lastSeen ? `last seen ${formatRelativeTime(lastSeen)}` : '—'}</span>
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-xs font-medium text-txt-tertiary shrink-0">{label}</span>
    <span className="text-xs text-txt-primary text-right min-w-0 break-words">{children}</span>
  </div>
)

export interface VisitorDetailCardProps {
  visitor: MapVisitor
  onClose: () => void
  /** Open the full "Visitor details" drawer (total time, chats, companies). */
  onViewDetails?: () => void
}

export const VisitorDetailCard: React.FC<VisitorDetailCardProps> = ({ visitor, onClose, onViewDetails }) => {
  const isOnline = visitor.status === 'online' || visitor.is_online
  const pageLabel = friendlyPageLabel(visitor.current_page_url, visitor.current_page_title)
  const name = visitorDisplayName(visitor)
  const location = visitorLocationLabel(visitor)

  return (
    <div className="rounded-xl bg-surface border border-border shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-border">
        <div className="flex items-start gap-3 min-w-0">
          <VisitorAvatar visitor={visitor} size={40} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-txt-primary truncate">{name}</p>
            {location && (
              <p className="text-xs text-txt-tertiary truncate">{location}</p>
            )}
            {visitor.company_name && (
              <p className="text-xs text-txt-secondary truncate mt-0.5">
                {visitor.company_name}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close visitor details"
          className="p-1 -mt-0.5 rounded-md text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors shrink-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-4 py-3">
        <div className="mb-2">
          <Badge variant="status" value={isOnline ? 'online' : 'offline'} />
        </div>

        {visitor.email && <Row label="Email">{visitor.email}</Row>}

        <Row label="On page">
          <span title={visitor.current_page_url || undefined}>{pageLabel}</span>
        </Row>
        <Row label="Came online">{formatLocalDateTimeWithTz(visitor.connected_at)}</Row>
        <Row label={isOnline ? 'Time online' : 'Session'}>
          <LiveDuration visitor={visitor} />
        </Row>

        {(visitor.browser || visitor.os || visitor.device_type) && (
          <Row label="Device">
            {[visitor.browser, visitor.os, visitor.device_type].filter(Boolean).join(' · ') || '—'}
          </Row>
        )}

        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            View details
          </button>
        )}
      </div>
    </div>
  )
}
